/**
 * Audit Trail viewer — searchable, filterable, paginated list of audit log
 * entries with a side-sheet showing the diff for the selected row.
 *
 * Read-only: the audit table is append-only by design.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ShieldCheck, Search } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatDate } from "@bizop/shared/dates";
import {
  Badge,
  DataTable,
  Input,
  KpiCard,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
} from "@bizop/ui-kit";

import { PageHeader } from "../../components/PageHeader";
import { ExportButton } from "../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../lib/export";
import { useMockData } from "../../lib/mockMode";
import { usePersistentMockState } from "../../lib/usePersistentMockState";
import {
  findAuditUser,
  mockAuditEntries,
  type MockAuditAction,
  type MockAuditEntry,
} from "../../lib/auditMock";
import { AuditActionBadge } from "../../components/audit/AuditActionBadge";
import { AuditDiffView } from "../../components/audit/AuditDiffView";

export const Route = createFileRoute("/_app/audit")({
  component: AuditPage,
});

const ACTION_OPTIONS: Array<{ value: MockAuditAction | "all"; label: string }> = [
  { value: "all", label: "All actions" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "status_change", label: "Status change" },
  { value: "approve", label: "Approve" },
  { value: "reject", label: "Reject" },
  { value: "void", label: "Void" },
  { value: "post", label: "Post" },
  { value: "reverse", label: "Reverse" },
];

function AuditPage() {
  const isMock = useMockData();
  void isMock;

  const [tableFilter, setTableFilter] = useState<string>("all");
  const [actionFilter, setActionFilter] = useState<MockAuditAction | "all">("all");
  const [actorFilter, setActorFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState<MockAuditEntry | null>(null);

  const [auditEntries] = usePersistentMockState<MockAuditEntry[]>(
    "auditEntries",
    mockAuditEntries,
  );

  const distinctTables = useMemo(
    () => Array.from(new Set(auditEntries.map((e) => e.tableName))).sort(),
    [auditEntries],
  );
  const distinctActors = useMemo(() => {
    const set = new Set<string>();
    for (const e of auditEntries) set.add(e.actorUserId ?? "system");
    return Array.from(set);
  }, [auditEntries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return auditEntries.filter((e) => {
      if (tableFilter !== "all" && e.tableName !== tableFilter) return false;
      if (actionFilter !== "all" && e.action !== actionFilter) return false;
      if (actorFilter !== "all") {
        const actor = e.actorUserId ?? "system";
        if (actor !== actorFilter) return false;
      }
      if (q) {
        const hay = `${e.tableName} ${e.recordId} ${e.notes ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [auditEntries, search, tableFilter, actionFilter, actorFilter]);

  // KPIs
  const last24h = useMemo(
    () =>
      auditEntries.filter(
        (e) => Date.now() - e.occurredAt < 24 * 60 * 60 * 1000,
      ).length,
    [auditEntries],
  );
  const distinctTablesCount = distinctTables.length;
  const distinctActorsCount = distinctActors.length;

  const columns = useMemo<ColumnDef<MockAuditEntry>[]>(
    () => [
      {
        accessorKey: "occurredAt",
        header: "When",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-secondary) tabular-nums">
            {formatDate(row.original.occurredAt, { style: "short", showTime: true })}
          </span>
        ),
      },
      {
        accessorKey: "actorUserId",
        header: "Actor",
        cell: ({ row }) => {
          const u = findAuditUser(row.original.actorUserId);
          return (
            <div className="flex flex-col">
              <span className="text-(--text-primary)">{u?.fullName ?? "—"}</span>
              {u?.userType === "system" ? (
                <span className="text-[10px] text-(--text-tertiary)">system</span>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: "action",
        header: "Action",
        cell: ({ row }) => <AuditActionBadge action={row.original.action} size="sm" />,
      },
      {
        accessorKey: "tableName",
        header: "Table",
        cell: ({ row }) => (
          <Badge variant="neutral" size="sm">
            {row.original.tableName}
          </Badge>
        ),
      },
      {
        accessorKey: "recordId",
        header: "Record",
        meta: { mono: true },
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.recordId}
          </span>
        ),
      },
      {
        accessorKey: "notes",
        header: "Notes",
        cell: ({ row }) => (
          <span className="text-(--text-tertiary) line-clamp-1">
            {row.original.notes ?? "—"}
          </span>
        ),
      },
    ],
    [],
  );

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<MockAuditEntry>[] = [
      {
        key: "occurredAt",
        label: "Occurred at",
        format: (v) => new Date(Number(v)).toISOString(),
      },
      {
        key: "actorUserId",
        label: "Actor",
        value: (r) => findAuditUser(r.actorUserId)?.fullName ?? "system",
      },
      {
        key: "actorUserId",
        label: "Actor type",
        value: (r) =>
          findAuditUser(r.actorUserId)?.userType ?? (r.actorUserId ? "" : "system"),
      },
      { key: "action", label: "Action" },
      { key: "tableName", label: "Table" },
      { key: "recordId", label: "Record" },
      { key: "ipAddress", label: "IP", format: (v) => (v == null ? "" : String(v)) },
      { key: "userAgent", label: "User agent", format: (v) => (v == null ? "" : String(v)) },
      { key: "notes", label: "Notes", format: (v) => (v == null ? "" : String(v)) },
      // Diffs as JSON columns so the spreadsheet still has the raw payload.
      { key: "beforeJson", label: "Before (JSON)", format: (v) => (v == null ? "" : String(v)) },
      { key: "afterJson", label: "After (JSON)", format: (v) => (v == null ? "" : String(v)) },
      { key: "diffJson", label: "Diff (JSON)", format: (v) => (v == null ? "" : String(v)) },
    ];
    downloadCsv(
      exportFilename("audit-trail", extension),
      toCsv(filtered, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Audit trail"
        description="Forensic record of every state-changing mutation. Append-only, immutable, searchable."
        actions={
          <ExportButton
            onCsv={() => handleExportCsv("csv")}
            onExcel={() => handleExportCsv("xls")}
          />
        }
      />

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Audit KPIs"
      >
        <KpiCard caption="Total entries" value={auditEntries.length} />
        <KpiCard caption="Last 24h" value={last24h} />
        <KpiCard caption="Tables touched" value={distinctTablesCount} />
        <KpiCard caption="Distinct actors" value={distinctActorsCount} />
      </section>

      {/* Filters */}
      <div
        className={cn(
          "flex flex-wrap items-end gap-3",
          "rounded-md border border-(--border-subtle) bg-(--bg-surface) p-3",
        )}
      >
        <FilterField label="Search">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-(--text-tertiary)" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search table, record, notes..."
              className="pl-8 w-[260px]"
            />
          </div>
        </FilterField>
        <FilterField label="Table">
          <Select value={tableFilter} onValueChange={setTableFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tables</SelectItem>
              {distinctTables.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Action">
          <Select
            value={actionFilter}
            onValueChange={(v) => setActionFilter(v as MockAuditAction | "all")}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTION_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Actor">
          <Select value={actorFilter} onValueChange={setActorFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Anyone</SelectItem>
              {distinctActors.map((id) => (
                <SelectItem key={id} value={id}>
                  {findAuditUser(id === "system" ? null : id)?.fullName ?? id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <div className="ml-auto text-[12px] text-(--text-tertiary) tabular-nums">
          Showing {filtered.length.toLocaleString("en-BD")} entries
        </div>
      </div>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetail(row.original)}
        emptyTitle="No audit entries match"
        emptyDescription="Try widening filters or clearing the search."
        emptyIcon={<ShieldCheck className="h-5 w-5" aria-hidden />}
      />

      <Sheet
        open={detail !== null}
        onOpenChange={(o) => {
          if (!o) setDetail(null);
        }}
      >
        <SheetContent side="right" size="lg">
          {detail ? <AuditDetail entry={detail} /> : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </span>
      {children}
    </label>
  );
}

function AuditDetail({ entry }: { entry: MockAuditEntry }) {
  const actor = findAuditUser(entry.actorUserId);
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <AuditActionBadge action={entry.action} />
          <span className="font-mono text-[14px]">{entry.tableName}</span>
        </SheetTitle>
        <SheetDescription className="font-mono">
          {entry.recordId}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-4">
          <section className="grid grid-cols-2 gap-3">
            <DetailField label="Actor" value={actor?.fullName ?? "—"} />
            <DetailField
              label="When"
              value={formatDate(entry.occurredAt, { style: "long", showTime: true })}
            />
            <DetailField
              label="IP"
              value={entry.ipAddress ?? "—"}
            />
            <DetailField
              label="User agent"
              value={
                entry.userAgent ? (
                  <span className="text-[11px] break-all">{entry.userAgent}</span>
                ) : (
                  "—"
                )
              }
            />
          </section>

          {entry.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                {entry.notes}
              </p>
            </section>
          ) : null}

          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Diff
            </div>
            <AuditDiffView
              beforeJson={entry.beforeJson}
              afterJson={entry.afterJson}
              diffJson={entry.diffJson}
            />
          </section>
        </div>
      </SheetBody>
    </>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary)">{value}</div>
    </div>
  );
}
